import { forwardRef, useRef, useState, type ElementType } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { Check, Plus, Search } from "lucide-react";

import { CONTROL_FOCUS_CLASS } from "../lib/controlStyles";
import { cn } from "../lib/cn";
import { Command, CommandGroup, CommandItem, CommandList } from "./Command";
import type { InputProps } from "./FormControls";
import { Popover, PopoverAnchor, PopoverContent } from "./Popover";

export interface AutocompleteOption {
  value: string;
  label?: string;
  icon?: ElementType;
}

export interface AutocompleteProps extends Omit<InputProps, "value" | "onChange" | "list"> {
  value: string;
  options: readonly AutocompleteOption[];
  onValueChange: (value: string) => void;
  suggestionsLabel?: string;
  customValueLabel?: string;
}

export const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>(function Autocomplete(
  {
    value,
    options,
    onValueChange,
    suggestionsLabel = "Suggestions",
    customValueLabel = "Use",
    autoComplete = "off",
    controlSize = "md",
    disabled,
    readOnly,
    className,
    onFocus,
    onBlur,
    onKeyDown,
    ...props
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelOpen = open && !disabled && !readOnly;
  const trimmed = value.trim();
  const query = trimmed.toLocaleLowerCase();
  const matches = options.filter((option) =>
    `${option.value} ${option.label ?? ""}`.toLocaleLowerCase().includes(query),
  );
  const hasExactMatch = options.some((option) => option.value === trimmed);

  function choose(next: string) {
    onValueChange(next);
    setOpen(false);
  }

  return (
    <div
      onKeyDown={(event) => {
        if (event.key === "Enter" && panelOpen) event.preventDefault();
      }}
    >
      <Command
        label={props["aria-label"] ?? "Suggestions"}
        shouldFilter={false}
        loop
        className="overflow-visible bg-transparent"
      >
        <Popover open={panelOpen} onOpenChange={setOpen}>
          <PopoverAnchor asChild>
            <div ref={anchorRef} className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
              />
              <CommandPrimitive.Input
                {...props}
                ref={ref}
                value={value}
                autoComplete={autoComplete}
                disabled={disabled}
                readOnly={readOnly}
                aria-expanded={Boolean(panelOpen)}
                className={cn(
                  "w-full rounded-control border border-control-border bg-surface pl-9 pr-3 text-primary transition-colors placeholder:text-muted hover:border-control-border-hover",
                  "disabled:cursor-not-allowed disabled:bg-surface-elevated disabled:text-muted disabled:opacity-70 read-only:bg-surface-elevated read-only:text-muted",
                  "aria-invalid:border-status-error aria-invalid:ring-2 aria-invalid:ring-status-error/20",
                  CONTROL_FOCUS_CLASS,
                  controlSize === "sm"
                    ? "density-control-sm text-xs"
                    : "density-control-md text-sm",
                  className,
                )}
                onValueChange={(next) => {
                  onValueChange(next);
                  setOpen(true);
                }}
                onFocus={(event) => {
                  onFocus?.(event);
                  setOpen(true);
                }}
                onBlur={(event) => {
                  onBlur?.(event);
                  setOpen(false);
                }}
                onKeyDown={(event) => {
                  onKeyDown?.(event);
                  if (event.defaultPrevented) return;
                  if (event.key === "Escape" && panelOpen) {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(false);
                  } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    setOpen(true);
                  }
                }}
              />
            </div>
          </PopoverAnchor>
          <PopoverContent
            align="start"
            className="w-(--radix-popover-trigger-width) max-w-[calc(100vw-2rem)] overflow-hidden p-0"
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            onMouseDown={(event) => event.preventDefault()}
            onInteractOutside={(event) => {
              if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
            }}
          >
            <CommandList className="max-h-[min(20rem,50vh)] p-1.5">
              {matches.length > 0 ? (
                <CommandGroup heading={suggestionsLabel}>
                  {matches.map((option) => {
                    const Icon = option.icon;
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={() => choose(option.value)}
                        className="gap-3 px-3 py-2.5"
                      >
                        {Icon ? <Icon aria-hidden className="size-4 shrink-0 text-muted" /> : null}
                        <span className="min-w-0 flex-1">
                          <span className="block break-words font-medium">
                            {option.label ?? option.value}
                          </span>
                          {option.label && option.label !== option.value ? (
                            <span className="block break-words text-xs text-muted">
                              {option.value}
                            </span>
                          ) : null}
                        </span>
                        {option.value === value ? (
                          <Check aria-hidden className="size-4 shrink-0 text-interaction-text" />
                        ) : null}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ) : null}
              {trimmed && !hasExactMatch ? (
                <CommandGroup className={matches.length ? "border-t border-border" : undefined}>
                  <CommandItem
                    value={trimmed}
                    onSelect={() => choose(trimmed)}
                    className="gap-3 px-3 py-2.5"
                  >
                    <Plus aria-hidden className="size-4 shrink-0 text-muted" />
                    <span className="min-w-0 break-words">
                      {customValueLabel} <span className="font-medium">{trimmed}</span>
                    </span>
                  </CommandItem>
                </CommandGroup>
              ) : null}
              {!trimmed && matches.length === 0 ? (
                <p className="px-3 py-4 text-sm text-muted">No suggestions</p>
              ) : null}
            </CommandList>
          </PopoverContent>
        </Popover>
      </Command>
    </div>
  );
});
